import { toColor } from "@/components/examinee/daily_interview_remind/DateService";
import {
  NOT_SELECTED,
  isPastRemindTime,
} from "@/components/examinee/daily_interview_remind/RemindService";
import {
  RemindItem,
  SimpleTime,
} from "@/components/examinee/daily_interview_remind/Types";
import {
  Divider,
  Select,
  SelectChangeEvent,
  SelectProps,
  Stack,
  Typography,
} from "@mui/material";
import { toStringDayOfWeek } from "@/utils/date";
import { format, isSameDay } from "date-fns";
import { ReactNode } from "react";

// 旧・通知メール設定画面（DailyInterviewRemind の RemindForm）と同等の選択肢（#1778）
const HOURS = [
  NOT_SELECTED,
  ...Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0")),
];
const MINUTES = [
  NOT_SELECTED,
  ...Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")),
];

type RemindTimeListFormProps = {
  remindItems: RemindItem[];
  onChangeHour: (index: number, hour: string) => void;
  onChangeMinutes: (index: number, minutes: string) => void;
  /**
   * 現在時刻。指定すると、日付が now と同日の行では now 以前になる時・分の選択肢を無効化し、
   * 選択済みの時刻が now 以前なら注意文を表示する（#1821: 前日開始時の 1 日目 = 本日）。
   * 省略時は従来どおり制約なし。
   */
  now?: Date;
};

/**
 * 8日分の「日付ラベル + 時セレクト + 分セレクト」リスト。
 * 旧・通知メール設定画面（DailyInterviewRemind）の RemindForm と同等の見た目・選択肢（#1778）。
 * now を渡すと本日の行で現在時刻以前の時刻を選べなくする（#1821）。
 */
export function RemindTimeListForm({
  remindItems,
  onChangeHour,
  onChangeMinutes,
  now,
}: RemindTimeListFormProps) {
  return (
    <Stack>
      <Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption">日付</Typography>
          <Typography variant="caption">受信時刻</Typography>
        </Stack>
        <Divider sx={{ mt: "8px", mb: "16px" }} />
      </Stack>
      <Stack spacing={6}>
        {remindItems.map((item, i) => {
          // 日付が本日の行だけ、現在時刻以前の時刻を選べないようにする（#1821）
          const todayNow =
            now != null && isSameDay(item.date, now) ? now : null;
          const isPast = (time: SimpleTime) =>
            todayNow != null && isPastRemindTime(item.date, time, todayNow);
          // 時: その時の最後の枠（:55）が過去なら、その時のすべての枠が過去
          const isHourDisabled = (h: string) =>
            h !== NOT_SELECTED && isPast(new SimpleTime(h, "55"));
          // 分: 時が選択済みのときだけ、その時・分の組み合わせで判定する
          const isMinutesDisabled = (m: string) =>
            m !== NOT_SELECTED &&
            item.time.hour !== NOT_SELECTED &&
            isPast(new SimpleTime(item.time.hour, m));
          const isSelectedTimePast = isPast(item.time);
          return (
            <Stack key={format(item.date, "yyyy-MM-dd")} spacing={1}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <DateTypography date={item.date} />
                <Stack direction="row" alignItems="center" spacing={2}>
                  <TimeSelect
                    value={item.time.hour}
                    onChange={(value) => onChangeHour(i, value)}
                  >
                    {HOURS.map((v) => (
                      <option value={v} key={v} disabled={isHourDisabled(v)}>
                        {v}
                      </option>
                    ))}
                  </TimeSelect>
                  <Typography variant="body2">時</Typography>
                  <TimeSelect
                    value={item.time.minutes}
                    onChange={(value) => onChangeMinutes(i, value)}
                  >
                    {MINUTES.map((v) => (
                      <option value={v} key={v} disabled={isMinutesDisabled(v)}>
                        {v}
                      </option>
                    ))}
                  </TimeSelect>
                  <Typography variant="body2">分頃</Typography>
                </Stack>
              </Stack>
              {isSelectedTimePast && (
                <Typography variant="caption" color="error" textAlign="right">
                  現在時刻より後の時刻を選択してください
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
}

type DateTypographyProps = {
  date: Date;
};
function DateTypography({ date }: DateTypographyProps) {
  const VARIANT = "subtitle1";
  return (
    <Stack direction="row" sx={{ alignItems: "center" }}>
      <Typography variant={VARIANT}>{format(date, "M月d日")}</Typography>
      <Typography variant={VARIANT}>(</Typography>
      <Typography variant={VARIANT} color={toColor(date)}>
        {toStringDayOfWeek(date)}
      </Typography>
      <Typography variant={VARIANT}>)</Typography>
    </Stack>
  );
}

type TimeSelectProps = {
  children: ReactNode;
  value: string;
  disabled?: SelectProps["disabled"];
  onChange: (value: string) => void;
};
function TimeSelect({ children, value, disabled, onChange }: TimeSelectProps) {
  return (
    <Select
      native
      value={value}
      size="small"
      disabled={disabled}
      sx={{
        select: {
          paddingLeft: "16px !important",
          paddingRight: "16px !important",
        },
        backgroundColor: "white",
      }}
      IconComponent={() => null}
      onChange={(event: SelectChangeEvent) => {
        onChange(event.target.value);
      }}
    >
      {children}
    </Select>
  );
}
